import React, { useState, useEffect, useCallback } from "react";
import { Modal, Form, message } from "antd";
import { useNavigate } from "react-router-dom";
import createInvoiceItemColumns from "../Utils/InvoiceItemColumns";
import InvoiceFormLayout from "./InvoiceFormLayout";
import {
  getAllPartners,
  getAllProducts,
  getAllCategories,
  getAccountTree,
  getCashById,
  createCashWithFile,
  updateCashWithFile,
  getMailingOffers,
  getAllBanks,
  clearAccountsCache,
} from "../../../api/accounting";
import { fetchBrandsBySelectedClients } from "../../../../../core/api/settings";
import { useSelector } from "react-redux";
import { getLists, getSegments } from "../../../../../core/api/mediaplan";
import {
  getOffersByBrand,
  getMarketingFilesByBrand,
} from "../../../../../core/api/marketing";
import dayjs from "dayjs";
import {
  calculateTotals,
  handleProductChange as handleProductChangeUtil,
  handleItemChange as handleItemChangeUtil,
  prepareFormData,
} from "../../../utils/invoiceUtils";
import { convertFromUSD, convertToUSD } from "../../../utils/currencyUtils";
import useDisplayCurrency from "../../../../../hooks/useDisplayCurrency";

const CreateCashModal = ({
  visible,
  onCancel,
  onSuccess,
  selectedBrand,
  cashId = null,
  defaultCashType = "cash",
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const selectedClients = useSelector((state) => state.selectedClients);
  const { decimalPrecision } = useDisplayCurrency();
  const [cashItems, setCashItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [mailingOffers, setMailingOffers] = useState([]);
  const [marketingFiles, setMarketingFiles] = useState([]);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [brands, setBrands] = useState([]);
  const [currentBrand, setCurrentBrand] = useState(selectedBrand);
  const [filteredBrands, setFilteredBrands] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [banks, setBanks] = useState([]);
  const [customRates, setCustomRates] = useState({});
  const [calculatedValues, setCalculatedValues] = useState({
    calculated_subtotal: 0,
    calculated_tax_amount: 0, // InvoiceFormLayout expects snake_case
    calculated_discount_amount: 0, // InvoiceFormLayout expects snake_case
    calculated_total: 0,
    match: true,
  });

  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);

  // No-op functions for payment terms (not used for cash payments)
  const noOp = () => {
    // Intentionally empty - payment terms are not used for cash payments
  };

  // Load custom currency rates from localStorage
  useEffect(() => {
    if (visible) {
      try {
        const savedRates = localStorage.getItem("custom_currency_rates");
        if (savedRates) {
          setCustomRates(JSON.parse(savedRates));
        }
      } catch (e) {
        console.error("Failed to load custom rates from localStorage", e);
      }
    }
  }, [visible]);

  // Fetch brands when selectedClients changes
  const fetchBrands = useCallback(async () => {
    if (selectedClients && selectedClients.length > 0) {
      try {
        const clientIds = Array.isArray(selectedClients)
          ? selectedClients.map((c) =>
              typeof c === "object" && c !== null ? c.id : c
            )
          : [
              typeof selectedClients === "object" && selectedClients !== null
                ? selectedClients.id
                : selectedClients,
            ];
        const response = await fetchBrandsBySelectedClients(
          clientIds.filter((id) => id !== undefined && id !== null)
        );
        if (response?.data) {
          setBrands(response.data);
          // If selectedBrand is provided and matches, keep it; otherwise use first brand
          if (selectedBrand) {
            const foundBrand = response.data.find(
              (b) => b.id === selectedBrand.id
            );
            if (foundBrand) {
              setCurrentBrand(foundBrand);
              setFilteredBrands([foundBrand]);
            } else if (response.data.length > 0) {
              setCurrentBrand(response.data[0]);
              setFilteredBrands([response.data[0]]);
            }
          } else if (response.data.length > 0) {
            setCurrentBrand(response.data[0]);
            setFilteredBrands([response.data[0]]);
          }
        }
      } catch (error) {
        console.error("Error fetching brands:", error);
      }
    }
  }, [selectedClients, selectedBrand]);

  const fetchPartners = useCallback(async () => {
    if (!currentBrand) return;
    try {
      const filters = { brand: currentBrand.id };
      const response = await getAllPartners(filters);
      setPartners(response.data || []);
    } catch (error) {
      console.error("Error fetching partners:", error);
      message.error("Failed to fetch partners");
    }
  }, [currentBrand]);

  const fetchProducts = useCallback(
    async (vendorId = null) => {
      if (!currentBrand) return;
      try {
        const filters = { brand: currentBrand.id };
        if (vendorId) filters.vendor = vendorId;
        const response = await getAllProducts(filters);
        setProducts(response.data || []);
      } catch (error) {
        console.error("Error fetching products:", error);
        message.error("Failed to fetch products");
      }
    },
    [currentBrand]
  );

  const fetchCategories = useCallback(
    async (vendorId = null) => {
      if (!currentBrand) return;
      try {
        const filters = { brand: currentBrand.id };
        if (vendorId) filters.vendor = vendorId;
        const response = await getAllCategories(filters);
        setCategories(response.data || []);
      } catch (error) {
        console.error("Error fetching categories:", error);
        message.error("Failed to fetch categories");
      }
    },
    [currentBrand]
  );

  // Accounts are GLOBAL - no brand filter needed
  const fetchAccounts = useCallback(async () => {
    try {
      // Accounts are global, fetch without brand filter
      const response = await getAccountTree({});
      // getAccountTree already returns response.data, so use response directly
      setAccounts(response || []);
    } catch (error) {
      console.error("Error fetching accounts:", error);
      message.error("Failed to fetch accounts");
    }
  }, []);

  const fetchOffers = useCallback(async () => {
    if (!currentBrand) return;
    try {
      const res = await getOffersByBrand(currentBrand.id);
      // Handle both response.data and direct response (defensive)
      const list = Array.isArray(res?.data) ? res.data : res;
      setOffers(list || []);
    } catch (error) {
      console.error("Error fetching offers:", error.response || error);
      // Don't show error message - offers are optional
      setOffers([]);
    }
  }, [currentBrand]);

  const fetchMailingOffers = useCallback(async () => {
    if (!currentBrand) return;
    try {
      const filters = { brand: currentBrand.id };
      const response = await getMailingOffers(filters);
      setMailingOffers(response.data || []);
    } catch (error) {
      console.error("Error fetching mailing offers:", error);
    }
  }, [currentBrand]);

  const fetchMarketingFiles = useCallback(async () => {
    if (!currentBrand) return;
    try {
      // Pass brandId directly, not as filters object
      const response = await getMarketingFilesByBrand(currentBrand.id);
      setMarketingFiles(response.data || []);
    } catch (error) {
      console.error("Error fetching marketing files:", error);
    }
  }, [currentBrand]);

  const fetchLists = useCallback(async () => {
    if (!currentBrand) return;
    try {
      const filters = { brand_id: currentBrand.id };
      const response = await getLists(filters);
      setLists(response.data || []);
    } catch (error) {
      console.error("Error fetching lists:", error);
    }
  }, [currentBrand]);

  const fetchSegments = useCallback(async (listId) => {
    if (!listId) {
      setSegments([]);
      return;
    }
    try {
      const response = await getSegments({ list_id: listId });
      setSegments(response.data || []);
    } catch (error) {
      console.error("Error fetching segments:", error);
    }
  }, []);

  const fetchBanks = useCallback(async () => {
    if (!currentBrand) return;
    try {
      const filters = { brand: currentBrand.id, active: true };
      const response = await getAllBanks(filters);
      setBanks(response.data || []);
    } catch (error) {
      console.error("Error fetching banks:", error);
      message.error("Failed to fetch banks");
    }
  }, [currentBrand]);

  const handleBrandChange = (brandId) => {
    const brand = brands.find((b) => b.id === brandId);
    if (brand) {
      setCurrentBrand(brand);
      setFilteredBrands([brand]);
      form.setFieldsValue({ brand: brandId });
    }
  };

  const handlePartnerChange = (partnerId) => {
    const partner = partners.find((p) => p.id === partnerId);
    setSelectedPartner(partner);
    // Update form field to ensure it's reflected immediately
    form.setFieldsValue({ partner: partnerId });
    if (partner) {
      if (
        partner.partner_type === "vendor" ||
        partner.partner_type === "both"
      ) {
        fetchProducts(partner.id);
        fetchCategories(partner.id);
      } else {
        fetchProducts();
        fetchCategories();
      }
    }
    // Refresh partners list to ensure we have the latest data
    fetchPartners();
  };

  const handleProductChange = (productId, itemKey) => {
    const updatedItems = handleProductChangeUtil(
      productId,
      itemKey,
      products,
      cashItems
    );
    if (updatedItems !== cashItems) {
      setCashItems(updatedItems);
      calculateCashTotals(updatedItems);
    }
  };

  const handleItemChange = (itemKey, field, value) => {
    const updatedItems = handleItemChangeUtil(itemKey, field, value, cashItems);
    setCashItems(updatedItems);
    calculateCashTotals(updatedItems);
  };

  const calculateCashTotals = useCallback(
    (items, formInstance) => {
      const itemsToCalculate = items || cashItems;
      const formToUse = formInstance || form;

      const totals = calculateTotals(itemsToCalculate, formToUse);
      setCalculatedValues(totals);

      // Update form total_amount to match calculated total
      formToUse.setFieldsValue({
        total_amount: totals.calculated_total,
        subtotal: totals.calculated_subtotal,
        discount_amount: totals.calculated_discount_amount,
        tax_rate: formToUse.getFieldValue("tax_rate") || 0,
      });
    },
    [cashItems, form]
  );

  const addItem = () => {
    const newItem = {
      key: Date.now(),
      productId: null,
      categoryId: null,
      accountId: null,
      description: "",
      quantity: 1,
      unitPrice: 0,
      taxRate: 0,
      taxAmount: 0,
      discountAmount: 0,
      subtotal: 0,
      total_amount: 0,
      productType: "product",
    };
    setCashItems([...cashItems, newItem]);
  };

  const removeItem = (itemKey) => {
    const updatedItems = cashItems.filter((item) => item.key !== itemKey);
    setCashItems(updatedItems);
    calculateCashTotals(updatedItems);
  };

  // Refs to track if data needs refresh when modal opens (persists across renders)
  const categoriesNeedRefreshRef = React.useRef(false);
  const accountsNeedRefreshRef = React.useRef(false);
  const partnersNeedRefreshRef = React.useRef(false);

  // Listen for category, account, and partner updates - always listen, refresh when modal opens
  useEffect(() => {
    const handleCategoriesUpdated = () => {
      if (visible && currentBrand) {
        // Refresh immediately if modal is visible
        fetchCategories();
      } else {
        // Mark for refresh when modal opens next
        categoriesNeedRefreshRef.current = true;
      }
    };
    const handleAccountsUpdated = () => {
      // Clear the API cache to ensure fresh data
      clearAccountsCache();
      if (visible) {
        // Refresh immediately if modal is visible
        fetchAccounts();
      } else {
        // Mark for refresh when modal opens next
        accountsNeedRefreshRef.current = true;
      }
    };

    const handlePartnersUpdated = () => {
      if (visible && currentBrand) {
        // Refresh partners immediately if modal is visible
        fetchPartners();
      } else {
        // Mark for refresh when modal opens next
        partnersNeedRefreshRef.current = true;
      }
    };

    window.addEventListener('categoriesUpdated', handleCategoriesUpdated);
    window.addEventListener('accountsUpdated', handleAccountsUpdated);
    window.addEventListener('partnersUpdated', handlePartnersUpdated);

    return () => {
      window.removeEventListener('categoriesUpdated', handleCategoriesUpdated);
      window.removeEventListener('accountsUpdated', handleAccountsUpdated);
      window.removeEventListener('partnersUpdated', handlePartnersUpdated);
    };
  }, [visible, currentBrand, fetchCategories, fetchAccounts, fetchPartners]);

  // Refresh categories/accounts/partners when modal opens if they were updated while closed
  useEffect(() => {
    if (visible) {
      // Always fetch fresh data when modal opens to ensure latest categories/accounts/partners
      if (currentBrand) {
        fetchCategories();
        fetchPartners();
        // Reset the refresh flags
        categoriesNeedRefreshRef.current = false;
        partnersNeedRefreshRef.current = false;
      }
      fetchAccounts();
      accountsNeedRefreshRef.current = false;
    }
  }, [visible, currentBrand, fetchCategories, fetchAccounts, fetchPartners]);

  useEffect(() => {
    if (visible) {
      fetchBrands();
    }
  }, [visible, fetchBrands]);

  useEffect(() => {
    if (selectedClients && selectedClients.length > 0) {
      fetchBrands();
    }
  }, [selectedClients, fetchBrands]);

  useEffect(() => {
    if (currentBrand) {
      form.setFieldsValue({
        cashType: defaultCashType, // InvoiceFormLayout will use typeFieldName prop
        currency: "$",
        issueDate: dayjs(),
        brand: currentBrand.id,
      });
      fetchPartners();
      fetchCategories();
      fetchAccounts();
      fetchOffers();
      fetchMailingOffers();
      fetchMarketingFiles();
      fetchLists();
      fetchBanks();
    } else {
      setPartners([]);
      setProducts([]);
      setCategories([]);
      setAccounts([]);
      setOffers([]);
      setMailingOffers([]);
      setMarketingFiles([]);
      setLists([]);
      setSegments([]);
      setBanks([]);
    }
  }, [
    currentBrand,
    fetchPartners,
    fetchCategories,
    fetchAccounts,
    fetchOffers,
    fetchMailingOffers,
    fetchMarketingFiles,
    fetchLists,
    fetchBanks,
    form,
    defaultCashType,
  ]);

  // Fetch cash data for editing
  const fetchCashData = useCallback(
    async (id) => {
      try {
        setLoading(true);
        const response = await getCashById(id);
        const cashData = response.data;

        if (cashData.brand) {
          let fullBrand = cashData.brand;
          if (
            typeof cashData.brand === "number" ||
            typeof cashData.brand === "string"
          ) {
            const foundBrand = brands.find(
              (b) => b.id === Number(cashData.brand)
            );
            if (foundBrand) {
              fullBrand = foundBrand;
            }
          } else if (cashData.brand.id) {
            const foundBrand = brands.find((b) => b.id === cashData.brand.id);
            fullBrand = foundBrand || cashData.brand;
          }
          setCurrentBrand(fullBrand);
          setFilteredBrands(fullBrand ? [fullBrand] : []);
        }

        if (cashData.partner) {
          // Handle partner - could be object or ID
          let partnerObj = cashData.partner;
          if (
            typeof cashData.partner === "number" ||
            typeof cashData.partner === "string"
          ) {
            // Find partner in current list or fetch if needed
            const foundPartner = partners.find(
              (p) => p.id === Number(cashData.partner)
            );
            if (foundPartner) {
              partnerObj = foundPartner;
            }
          }
          setSelectedPartner(partnerObj);
        }

        // Ensure partners are fetched for the current brand
        if (currentBrand) {
          fetchPartners();
        }

        // Convert amounts from USD back to original currency for display
        const currency = cashData.currency || "$";
        const isUSD = currency === "$" || currency === "USD";

        const formattedItems = (cashData.items || []).map((item) => {
          // Convert from USD to original currency if needed
          const round3 = (val) => Number(Number(val || 0).toFixed(decimalPrecision));
          const unitPrice = isUSD
            ? round3(item.unit_price)
            : round3(convertFromUSD(item.unit_price, currency, customRates));
          const taxAmount = isUSD
            ? round3(item.tax_amount)
            : round3(convertFromUSD(item.tax_amount, currency, customRates));
          const discountAmount = isUSD
            ? round3(item.discount_amount || 0)
            : round3(
                convertFromUSD(item.discount_amount || 0, currency, customRates)
              );
          const subtotal = isUSD
            ? round3(item.subtotal)
            : round3(convertFromUSD(item.subtotal, currency, customRates));
          const total = isUSD
            ? round3(item.total)
            : round3(convertFromUSD(item.total, currency, customRates));

          return {
            key: item.id,
            productId: item.product,
            categoryId: item.category,
            accountId: item.account || null,
            description: item.description,
            quantity: item.quantity,
            unitPrice: unitPrice,
            taxRate: item.tax_rate,
            taxAmount: taxAmount,
            discountAmount: discountAmount,
            subtotal: subtotal,
            total_amount: total,
            productType: item.product_type || "product",
            printType: item.print_type || null,
            envelope: item.envelope || null,
            marketingOffer: item.marketing_offer || null,
            listId: item.list || null,
            segmentId: item.segment || null,
          };
        });

        // Convert invoice-level amounts from USD back to original currency for display
        const round3 = (val) => Number(Number(val || 0).toFixed(decimalPrecision));
        const displaySubtotal = isUSD
          ? round3(cashData.subtotal || 0)
          : round3(
              convertFromUSD(cashData.subtotal || 0, currency, customRates)
            );
        const displayDiscountAmount = isUSD
          ? round3(cashData.discount_amount || 0)
          : round3(
              convertFromUSD(
                cashData.discount_amount || 0,
                currency,
                customRates
              )
            );
        const displayTotalAmount = isUSD
          ? round3(cashData.total_amount || 0)
          : round3(
              convertFromUSD(cashData.total_amount || 0, currency, customRates)
            );

        form.setFieldsValue({
          partner: cashData.partner?.id || cashData.partner,
          brand: cashData.brand?.id || cashData.brand,
          issueDate: dayjs(cashData.issue_date),
          cashType: cashData.cash_type,
          number: cashData.number,
          currency: cashData.currency || "$",
          notes: cashData.notes,
          bank: cashData.bank?.id || cashData.bank || null,
          subtotal: displaySubtotal,
          discount_amount: displayDiscountAmount,
          tax_rate: cashData.tax_rate || 0,
          total_amount: displayTotalAmount,
          fileUrl: cashData.file || null,
        });

        setCashItems(formattedItems);
        const calculated = calculateTotals(formattedItems, form);
        setCalculatedValues(calculated);
        setIsEditMode(true);
      } catch (error) {
        console.error("Error fetching cash data:", error);
        message.error("Failed to load cash payment data");
      } finally {
        setLoading(false);
      }
    },
    [brands, form, currentBrand, partners, fetchPartners, decimalPrecision]
  );

  useEffect(() => {
    if (cashId && !isEditMode && brands.length > 0) {
      setIsEditMode(true);
      fetchCashData(cashId);
    }
  }, [cashId, isEditMode, brands, fetchCashData]);

  const handleCancel = () => {
    form.resetFields();
    setCashItems([]);
    setCalculatedValues({
      calculated_subtotal: 0,
      calculated_tax_amount: 0,
      calculated_discount_amount: 0,
      calculated_total: 0,
      match: true,
    });
    setIsEditMode(false);
    onCancel();
  };

  const onFinish = async (values) => {
    if (!currentBrand) {
      message.error("Please select a brand before saving the cash payment");
      return;
    }

    if (cashItems.length === 0) {
      message.error("Please add at least one item");
      return;
    }

    await submitCash(values);
  };

  const submitCash = async (values) => {
    setLoading(true);
    try {
      const cashData = {
        ...values,
        cash_type: values.cashType || values.cash_type || defaultCashType,
        issue_date:
          values.issueDate || values.issue_date
            ? (values.issueDate || values.issue_date).format("YYYY-MM-DD")
            : null,
        brand: currentBrand.id,
        number: values.number,
        currency: values.currency || "USD",
        bank: values.bank || null,
        items: cashItems.map((item) => ({
          product: item.productId,
          category: item.categoryId,
          account: item.accountId,
          description: item.description,
          quantity: item.quantity,
          unit_price: item.unitPrice,
          tax_rate: item.taxRate,
          tax_amount: item.taxAmount,
          discount_amount: item.discountAmount,
          subtotal: item.subtotal,
          total:
            item.total_amount ||
            item.subtotal + item.taxAmount - item.discountAmount,
          product_type: item.productType || "product",
          print_type: item.printType || null,
          envelope: item.envelope || null,
          marketing_offer: item.marketingOffer || null,
          list: item.listId || null,
          segment: item.segmentId || null,
        })),
      };

      // CRITICAL: Convert ALL amounts to USD if currency is not USD
      // Everything in the database should be stored in USD for consistency
      const currency = values.currency || "$";
      const isUSD = currency === "$" || currency === "USD";

      let finalCashData = { ...cashData };
      // Ensure bank is always included (even if null) so it can be cleared
      finalCashData.bank = values.bank || null;
      const round3 = (val) => Number(Number(val || 0).toFixed(3));

      if (!isUSD) {
        // Convert cash-level amounts to USD and round to 3 decimals
        // Use calculatedValues or form values for cash-level totals
        const cashSubtotal =
          calculatedValues.calculated_subtotal ||
          values.subtotal ||
          cashData.subtotal ||
          0;
        const cashDiscountAmount =
          calculatedValues.calculated_discount_amount ||
          values.discount_amount ||
          cashData.discount_amount ||
          0;
        const cashTotalAmount =
          calculatedValues.calculated_total ||
          values.total_amount ||
          cashData.total_amount ||
          0;

        finalCashData.subtotal = round3(
          convertToUSD(cashSubtotal, currency, customRates)
        );
        finalCashData.discount_amount = round3(
          convertToUSD(cashDiscountAmount, currency, customRates)
        );
        finalCashData.total_amount = round3(
          convertToUSD(cashTotalAmount, currency, customRates)
        );

        // Convert item-level amounts to USD and round to 3 decimals
        finalCashData.items = cashData.items.map((item) => ({
          ...item,
          unit_price: round3(
            convertToUSD(item.unit_price || 0, currency, customRates)
          ),
          subtotal: round3(
            convertToUSD(item.subtotal || 0, currency, customRates)
          ),
          tax_amount: round3(
            convertToUSD(item.tax_amount || 0, currency, customRates)
          ),
          discount_amount: round3(
            convertToUSD(item.discount_amount || 0, currency, customRates)
          ),
          total: round3(convertToUSD(item.total || 0, currency, customRates)),
        }));
      }

      const formData = prepareFormData(finalCashData, values);

      if (isEditMode) {
        await updateCashWithFile(cashId, formData);
        message.success("Cash payment updated successfully");
      } else {
        await createCashWithFile(formData);
        message.success("Cash payment created successfully");
      }

      onSuccess?.();
      handleCancel();
    } catch (error) {
      console.error("Error submitting cash payment:", error);
      message.error(
        error.response?.data?.error ||
          error.response?.data?.cash_type?.[0] ||
          `Failed to ${isEditMode ? "update" : "create"} cash payment`
      );
    } finally {
      setLoading(false);
    }
  };

  const handleFormValuesChange = useCallback(
    (changedValues) => {
      if (
        "discount_amount" in changedValues ||
        "tax_rate" in changedValues ||
        "total_amount" in changedValues
      ) {
        calculateCashTotals();
      }
    },
    [calculateCashTotals]
  );

  const getCurrentCurrency = () => form.getFieldValue("currency") || "$";

  const columns = createInvoiceItemColumns(
    products,
    categories,
    accounts,
    offers,
    handleProductChange,
    handleItemChange,
    getCurrentCurrency,
    setCashItems,
    calculateCashTotals,
    removeItem,
    getCurrentCurrency(),
    mailingOffers,
    marketingFiles,
    cashItems,
    lists,
    segments,
    fetchSegments,
    navigate
  );

  return (
    <Modal
      title={isEditMode ? "Edit Cash Payment" : "Create Cash Payment"}
      open={visible}
      onCancel={handleCancel}
      footer={null}
      width="95%"
      style={{ top: 20 }}
      zIndex={2000}
      destroyOnClose
    >
      <div style={{ maxHeight: "80vh", overflowY: "auto" }}>
        <InvoiceFormLayout
          form={form}
          isEditMode={isEditMode}
          loading={loading}
          invoiceItems={cashItems}
          setInvoiceItems={setCashItems}
          partners={partners}
          paymentTerms={[]}
          setPaymentTerms={noOp}
          categories={categories}
          products={products}
          accounts={accounts}
          offers={offers}
          columns={columns}
          calculatedValues={calculatedValues}
          setCalculatedValues={setCalculatedValues}
          onFinish={onFinish}
          handlePartnerChange={handlePartnerChange}
          handleProductChange={handleProductChange}
          handleItemChange={handleItemChange}
          calculateInvoiceTotals={calculateCashTotals}
          addItem={addItem}
          addPaymentTerm={noOp}
          removePaymentTerm={noOp}
          updatePaymentTerm={noOp}
          navigate={navigate}
          selectedBrand={currentBrand}
          onValuesChange={handleFormValuesChange}
          brands={brands}
          onBrandChange={handleBrandChange}
          defaultInvoiceType={defaultCashType}
          hidePaymentTerms={true}
          typeFieldName="cashType"
          typeOptions={[
            { value: "cash", label: "Cash" },
            { value: "check", label: "Check" },
          ]}
          typeLabel="Cash Type"
          numberLabel="Reference Number"
          numberPlaceholder="e.g. CASH-2025-001"
          fileList={[]}
          // Cash-specific labels
          formTitle={isEditMode ? "Edit Cash Payment" : "Create Cash Payment"}
          itemsLabel="Cash Payment Items"
          fileLabel="Upload Cash Payment File"
          submitLabel={
            isEditMode ? "Update Cash Payment" : "Create Cash Payment"
          }
          cancelPath="/accounting/cash"
          // Bank field for cash payments
          banks={banks}
          showBankField={true}
        />
      </div>
    </Modal>
  );
};

export default CreateCashModal;
